import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useCreateMainComponent, invalidateMainComponentCache } from '../../../api/main-component'
import { invalidateMainComponentTypeCache } from '../../../api/main-component-type'
import { IMainComponent } from '../../../generated-types/main-component'
import { ShowDialogProps } from '../../../show-dialog'
import MainComponentForm from './MainComponentForm'
import { getTranslatedText } from '../../../utils/translations'
import { invalidateStorageCache } from 'src/api/storage'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateComponentGetByStatus, invalidateServiceStationCache } from 'src/api/service-station'
import { MainComponentStatus } from 'src/enums'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { invalidateDocument } from 'src/api/document'

interface CreateMainComponentDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId?: EntityId
  componentTypeId?: number
  mainComponentTypeName?: string
  initialValues?: IMainComponent
  storageId?: EntityId
  searchText?: string
  clonedFromEquipment?: IMainComponent
  serviceStationId?: EntityId
  isCustomerSelectionEnable?: boolean
}
export const CreateMainComponentDialog: React.FC<CreateMainComponentDialogProps> = ({
  pop,
  customerId,
  localityId,
  componentTypeId,
  mainComponentTypeName,
  initialValues,
  storageId,
  searchText,
  clonedFromEquipment,
  serviceStationId,
  isCustomerSelectionEnable,
}) => {
  const [selectedCustomerId, setSelectedCustomerId] = useState<EntityId | undefined>()
  const { t } = useTranslation(['main-component', 'common', 'certificate'])
  const { mutateAsync, error, data } = useCreateMainComponent(customerId)
  const [clonedEquipment, setClonedEquipment] = useState<IMainComponent>()

  useEffect(() => {
    if (error != null) {
      if (error['data']['message'] == 'SameMainComponentName') {
        toast(t('sameMainComponentName'), { type: 'error' })
      }
      if (error['data']['message'] == 'SerialNumberExists') {
        toast(t('sameSerialNumber'), { type: 'error' })
      }
      if (error['data']['message'] == 'NotAValidCertificate') {
        toast(t('NotAValidCertificate', { ns: 'certificate' }), { type: 'error' })
      }
      if (error['data']['message'] == 'ValidUntilLessThanCertificateDate') {
        toast(t('ValidUntilLessThanCertificateDate', { ns: 'certificate' }), { type: 'error' })
      }
    }
  }, [error, t])

  useEffect(() => {
    setClonedEquipment(data?.data.data)
  }, [data])

  const resetFormData = form => {
    form.setValue('typeId', null)
    form.setValue('supplierId', null)
    form.setValue('positionId', null)
    form.setValue('customPropertyValues', '{}')
    form.setValue('mainComponentMaintenanceTemplates', undefined)
  }

  const onSubmit = async (form, isCloneable = false, formContext) => {
    await mutateAsync(
      {
        ...form,
        selectedLocalityId: localityId,
        storageId: storageId,
        ClonedFromEquipmentId: clonedFromEquipment?.id ?? clonedEquipment?.id,
        ClonedFromEquipmentIdSerialNumber: clonedFromEquipment?.serialNumber ?? clonedEquipment?.serialNumber,
        serviceStationId: serviceStationId,
        customerId: isCustomerSelectionEnable ? selectedCustomerId : undefined,
      },
      {
        onSuccess: data => {
          toast(t('successfullyAdded', { ns: 'common-api-message' }), { type: 'success' })
          invalidateMainComponentCache.getMainComponentsByComponentType(
            selectedCustomerId ?? customerId,
            form.typeId as EntityId,
            searchText,
          )
          invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(
            selectedCustomerId ?? customerId,
          )
          invalidateMainComponentCache.invalidateEquipment(selectedCustomerId ?? customerId, { localityId })
          invalidateStorageCache.getEquipmentInStorage(customerId, storageId as EntityId)
          invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(
            selectedCustomerId ?? customerId,
            searchText as string,
          )
          invalidationActivityLogCache.getCageActivityLogs()
          invalidateServiceStationCache.getComponentsByStatus(
            customerId as EntityId,
            serviceStationId as EntityId,
            MainComponentStatus.ReadyToDelivery,
          )
          invalidateComponentGetByStatus(
            customerId as EntityId,
            serviceStationId as EntityId,
            MainComponentStatus.ReadyToDelivery,
          )
          invalidateServiceStationCache.searchCustomersForGlobalServiceStation(customerId as EntityId, serviceStationId)
          invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
          invalidateDocument.getDocuments(customerId)
          invalidateDocument.getDocumentsForApproval(customerId, localityId)
          if (!isCloneable) {
            pop()
          }
        },
        onError: error => {
          if (error['data']['message'] == 'CertificateNumberExists') {
            toast.error(t('certificateNumberExists', { ns: 'main-component' }))
            if (formContext && typeof formContext.setError === 'function') {
              formContext.setError('certificateNumber', {
                type: 'manual',
                message: t('certificateNumberExists', { ns: 'main-component' }),
              })
            }
          }
        },
      },
    )
  }

  return (
    <MainComponentForm
      title={
        mainComponentTypeName
          ? `${t('addMainComponent')} - ${getTranslatedText(mainComponentTypeName)}`
          : t('addMainComponent')
      }
      onSubmit={onSubmit}
      onSubmitAlter={onSubmit}
      onCancel={() => pop()}
      initialValues={
        initialValues ??
        ({
          mainComponentTypeName: mainComponentTypeName,
          typeId: componentTypeId,
        } as IMainComponent)
      }
      customerId={customerId}
      selectedCustomerId={selectedCustomerId}
      setSelectedCustomerId={setSelectedCustomerId}
      serviceStationId={serviceStationId}
      isCustomerSelectionEnable={isCustomerSelectionEnable}
      resetFormData={resetFormData}
    />
  )
}
